/**
 * Accounting Settings — beta-local controls.
 *
 * What lives here:
 *   • Materiality floor (drives anomaly review queue threshold)
 *   • Read-event audit log viewer (companion to write-side Audit Log)
 *
 * Both are frontend-only today. Materiality is per-browser via localStorage;
 * read events are in-memory. When Cloud lands, the IO swaps to Supabase
 * without changing the surface.
 */
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  getMaterialityFloor,
  setMaterialityFloor,
} from "@/data/accounting/settings/accountingSettings";
import { getReadEvents, subscribeReadEvents } from "@/data/accounting/audit/readEventLog";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { useLogReadOnMount } from "@/hooks/useLogReadOnMount";

export default function AccountingSettingsPage() {
  useLogReadOnMount("accounting-settings", { sensitivity: "default" });

  const [floor, setFloor] = useState<number>(getMaterialityFloor());
  const [saved, setSaved] = useState(false);

  // subscribe to the read-event log so the viewer reflects new entries live
  const [tick, setTick] = useState(0);
  useEffect(() => subscribeReadEvents(() => setTick(t => t + 1)), []);
  const events = useMemo(() => getReadEvents().slice(0, 50), [tick]);

  const handleSave = () => {
    setMaterialityFloor(floor);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1800);
  };

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <BooksNavRail />
      <div>
        <h1 className="text-2xl font-semibold" data-tour-id="accounting-settings">
          Accounting Settings
        </h1>
        <p className="text-sm text-muted-foreground">
          Tune the integrity engine and view who's been reading what.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Materiality floor</CardTitle>
          <CardDescription>
            Anomalies below this dollar magnitude are hidden from the Review Queue.
            Raise this if the queue is too noisy; lower it if small fraud risk concerns you.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-end gap-3">
            <div className="flex-1 max-w-xs">
              <Label htmlFor="floor">USD</Label>
              <Input
                id="floor"
                type="number"
                min={0}
                step={50}
                value={floor}
                onChange={(e) => setFloor(Number(e.target.value) || 0)}
              />
            </div>
            <Button onClick={handleSave}>Save</Button>
            {saved && <Badge variant="secondary">Saved</Badge>}
          </div>
          <p className="text-xs text-muted-foreground">
            Default $250. Commercial GCs typically raise to $1,000+.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Read-event audit log</CardTitle>
          <CardDescription>
            In-memory log of who viewed sensitive surfaces (margin, payouts, contract values).
            Persists to Supabase once Cloud is wired.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No read events captured yet. Visit a sensitive page (e.g. Job Profitability) to populate.
            </p>
          ) : (
            <div className="rounded-md border divide-y text-sm">
              {events.map(ev => (
                <div key={ev.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{ev.surface}</div>
                    {ev.entityRef && (
                      <div className="text-xs text-muted-foreground truncate">
                        {ev.entityRef.kind}: {ev.entityRef.id}
                      </div>
                    )}
                  </div>
                  <Badge variant="outline" className="text-xs">{ev.sensitivity}</Badge>
                  <div className="text-xs text-muted-foreground w-32 text-right">
                    {ev.viewerRole ?? "unknown"}
                  </div>
                  <div className="text-xs text-muted-foreground w-44 text-right tabular-nums">
                    {new Date(ev.at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
